import React from "react";
import styled from "styled-components";

import { Button as BAIButton } from "js/Components/Button";
import { Icon } from "js/Components/Icon";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { AiIcon } from "js/editor/AiGeneration/AiIcon";
import { FilterDropDown } from "js/react/views/AddSlide/Panes/Components/FilterDropDown";
import { SearchBar } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { themeColors } from "js/react/sharedStyles";
import { t } from "js/i18n";

export interface SearchAndAddFilterOption {
    field: string;
    label: string;
}

export interface SearchAndAddAction {
    id?: string;
    label: string;
    onClick: () => void;
    variant: "ai" | "primary" | "upgrade";
}

export interface SearchAndAddProps {
    actions?: SearchAndAddAction[];
    className?: string;
    contentFilterOptions?: SearchAndAddFilterOption[];
    onSearch: (value: string) => void;
    onContentFilterChange?: (event: { target: { value: string } }) => void;
    onTagFilterChange?: (event: { target: { value: string } }) => void;
    searchPlaceholder?: string;
    searchValue?: string;
    selectedContentFilter?: string;
    selectedTagFilter?: string;
    tagFilterOptions?: SearchAndAddFilterOption[];
}

const Row = styled.div`
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 16px;
`;

const SearchFrame = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    background: white;
    box-sizing: border-box;
`;

const StyledSearchBar = styled(SearchBar)`
    flex: 1;
    min-width: 0;
    margin: 0;
    padding: 6px 10px;
    gap: 10px;
    border: none;
    background: transparent;
    box-sizing: border-box;

    .MuiIcon-root {
        color: ${themeColors.ui_blue};
    }

    .clear-search {
        right: 12px;
        margin-right: 0;
    }

    input[type="text"] {
        width: 100%;
        margin: 0;
        font-size: 16px;
        font-weight: 500;
        padding-right: 24px;
    }

    input[type="text"]::placeholder {
        color: #999;
        font-family: "Source Sans Pro";
        font-size: 16px;
        font-weight: 400;
        line-height: 150%;
    }
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    margin-left: auto;
`;

const StyledBAIButton = styled(BAIButton)`
    padding: 6px 20px;
    border-radius: 3px;
`;

function SearchAndAddActionButton({ action }: { action: SearchAndAddAction }) {
    if (action.variant === "ai") {
        return (
            <StyledBAIButton large ai onClick={action.onClick} id={action.id} style={{ flexShrink: 0 }}>
                <AiIcon />
                {action.label}
            </StyledBAIButton>
        );
    }

    if (action.variant === "upgrade") {
        return (
            <StyledBAIButton large upgrade onClick={action.onClick} id={action.id} style={{ flexShrink: 0 }}>
                <UpgradeIcon />
                {action.label}
            </StyledBAIButton>
        );
    }

    return (
        <StyledBAIButton blue large onClick={action.onClick} id={action.id} style={{ flexShrink: 0 }}>
            <Icon white fill>add_circle</Icon>
            {action.label}
        </StyledBAIButton>
    );
}

export function SearchAndAdd({
    actions = [],
    className,
    contentFilterOptions,
    onSearch,
    onContentFilterChange,
    onTagFilterChange,
    searchPlaceholder = t("Search..."),
    searchValue,
    selectedContentFilter,
    selectedTagFilter,
    tagFilterOptions,
}: SearchAndAddProps) {
    const showContentFilter = Boolean(contentFilterOptions && onContentFilterChange && selectedContentFilter != null);
    const showTagFilter = Boolean(tagFilterOptions && onTagFilterChange && selectedTagFilter != null);

    return (
        <Row className={className}>
            <SearchFrame>
                <StyledSearchBar
                    placeholder={searchPlaceholder}
                    onSearch={onSearch}
                    query={searchValue}
                />
                {(showContentFilter || showTagFilter) && (
                    <>
                        {showContentFilter && (
                            <FilterDropDown
                                onChange={onContentFilterChange!}
                                selectedFilter={selectedContentFilter!}
                                filterOptions={contentFilterOptions!}
                            />
                        )}
                        {showTagFilter && (
                            <FilterDropDown
                                onChange={onTagFilterChange!}
                                selectedFilter={selectedTagFilter!}
                                filterOptions={tagFilterOptions!}
                            />
                        )}
                    </>
                )}
            </SearchFrame>
            {actions.length > 0 && (
                <Actions>
                    {actions.map(action => (
                        <SearchAndAddActionButton
                            key={`${action.variant}-${action.label}`}
                            action={action}
                        />
                    ))}
                </Actions>
            )}
        </Row>
    );
}
